import { useState, useEffect } from 'react'
import { PlusCircle, Search, MoreHorizontal } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table'
import { Badge } from '../../components/ui/badge'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '../../components/ui/dropdown-menu'
import { useAuth } from '@/contexts/auth-context'
import { supabase } from '@/utils/supabase/client'
import { InventoryItem, Supplier, InventoryCategory } from '@/types'
import { InventoryFormModal } from '@/components/inventory-form-modal'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { SupplierList } from '@/components/supplier-list'
import { CategoryList } from '@/components/category-list'
import { ConfirmationDialog } from '@/components/confirmation-dialog'

// Main component for the Inventory Page
export default function InventoryPage() {
  const [items, setItems] = useState<InventoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const { currentOrganization } = useAuth()

  useEffect(() => {
    if (currentOrganization) {
      fetchInventoryItems()
    }
  }, [currentOrganization])

  const fetchInventoryItems = async () => {
    if (!currentOrganization) return
    setLoading(true)
    const { data, error } = await supabase
      .from('inventory_items')
      .select('*, inventory_categories(*), suppliers(*)')
      .eq('organization_id', currentOrganization.id)
      
    if (data) {
      // The select query casts category and supplier, so we need to fix the type
      const typedData = data.map(d => ({
        ...d,
        categories: d.inventory_categories,
        suppliers: d.suppliers
      }))
      setItems(typedData as any)
    }
    setLoading(false)
  }

  const handleSave = () => {
    fetchInventoryItems()
  }

  const handleEdit = (item: InventoryItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  }

  const handleDelete = (item: InventoryItem) => {
    setSelectedItem(item);
    setIsConfirmOpen(true);
  }

  const confirmDelete = async () => {
    if (!selectedItem) return;
    await supabase.from('inventory_items').delete().eq('id', selectedItem.id)
    fetchInventoryItems();
    setIsConfirmOpen(false);
    setSelectedItem(null);
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Inventory Management</CardTitle>
          <CardDescription>Track and manage your clinic's supplies and pharmaceuticals.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="products">
            <TabsList>
              <TabsTrigger value="products">Products</TabsTrigger>
              <TabsTrigger value="categories">Categories</TabsTrigger>
              <TabsTrigger value="suppliers">Suppliers</TabsTrigger>
            </TabsList>
            <TabsContent value="products">
              <div className="flex justify-between items-center mb-4 mt-4">
                <div className="relative w-full max-w-sm">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                  <Input type="search" placeholder="Search products..." className="pl-8" />
                </div>
                <Button onClick={() => {
                  setSelectedItem(null);
                  setIsModalOpen(true);
                }}>
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </div>

              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map(item => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell>{item.categories?.name ?? 'N/A'}</TableCell>
                        <TableCell>{item.suppliers?.name ?? 'N/A'}</TableCell>
                        <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                        <TableCell className="text-right">
                          {item.price ? `Rs. ${item.price.toLocaleString()}`: 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={item.quantity > item.low_stock_threshold ? 'default' : 'destructive'}>
                            {item.quantity > item.low_stock_threshold ? 'In Stock' : 'Low Stock'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button aria-haspopup="true" size="icon" variant="ghost">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Toggle menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem onSelect={() => handleEdit(item)}>Edit</DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => handleDelete(item)}>Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
            <TabsContent value="categories">
              <CategoryList />
            </TabsContent>
            <TabsContent value="suppliers">
              <SupplierList />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      <InventoryFormModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        item={selectedItem}
      />
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Are you sure?"
        description="This action cannot be undone. This will permanently delete the item."
      />
    </>
  )
} 